// src/components/admin/SupportSettingsManagement.js
import React, { useState, useEffect, useCallback } from 'react';
import { helpContentService } from '../../services/helpContentService';
import LoadingSpinner from '../common/LoadingSpinner';
import Toast from '../common/Toast';

const FIELDS = [
    { key: 'support_email', label: 'Support Email', placeholder: 'support@ehaazri.com', type: 'email' },
    { key: 'support_phone', label: 'Support Phone', placeholder: '+234 800 000 0000', type: 'text' },
    { key: 'support_hours_days', label: 'Support Hours (Days)', placeholder: 'Monday - Friday', type: 'text' },
    { key: 'support_hours_time', label: 'Support Hours (Time)', placeholder: '9:00 AM - 6:00 PM (WAT)', type: 'text' }
];

const SupportSettingsManagement = () => {
    const [values, setValues] = useState({});
    const [loading, setLoading] = useState(true);
    const [saveLoading, setSaveLoading] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    const showToast = (message, type = 'success') => setToast({ show: true, message, type });

    const loadSettings = useCallback(async () => {
        try {
            setLoading(true);
            const response = await helpContentService.getAppSettings();
            const settings = response.settings || [];
            const map = {};
            settings.forEach(setting => {
                map[setting.setting_key] = setting.setting_value;
            });
            setValues(map);
        } catch (error) {
            showToast(error.message, 'error');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    const handleChange = (key, value) => {
        setValues(prev => ({ ...prev, [key]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            setSaveLoading(true);
            const settings = FIELDS.map(field => ({
                setting_key: field.key,
                setting_value: values[field.key] || ''
            }));

            await helpContentService.updateAppSettings(settings);
            showToast('Support settings updated successfully', 'success');
        } catch (error) {
            showToast(error.message, 'error');
        } finally {
            setSaveLoading(false);
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '40px' }}>
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <div>
            <Toast
                show={toast.show}
                message={toast.message}
                type={toast.type}
                onClose={() => setToast({ show: false, message: '', type: 'success' })}
            />

            <div style={{ marginBottom: '20px' }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#333' }}>
                    Support Settings
                </h2>
                <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '13px' }}>
                    Contact details shown in the mobile app's Contact Support screen
                </p>
            </div>

            <form onSubmit={handleSubmit} style={{ maxWidth: '500px' }}>
                {FIELDS.map(field => (
                    <div key={field.key} style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                            {field.label}
                        </label>
                        <input
                            type={field.type}
                            value={values[field.key] || ''}
                            onChange={(e) => handleChange(field.key, e.target.value)}
                            placeholder={field.placeholder}
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px'
                            }}
                        />
                    </div>
                ))}

                <button
                    type="submit"
                    style={{
                        padding: '10px 20px',
                        backgroundColor: '#007bff',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: saveLoading ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        opacity: saveLoading ? 0.7 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                    disabled={saveLoading}
                >
                    {saveLoading && <LoadingSpinner size="small" />}
                    {saveLoading ? 'Saving...' : 'Save Settings'}
                </button>
            </form>
        </div>
    );
};

export default SupportSettingsManagement;
